import { useMediaQuery } from '@components/Common/DesktopOnly';
import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import ErrorMessage from '@components/Common/ErrorMessage';
import EmptyState from '@components/Common/EmptyState';
import FormField from '@components/Common/FormField';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { hasTeamView, useCurrentUserRole } from '@components/Common/Utils/roles';
import { APPLICATION_STAGES, STATUS_OPTIONS, resolveStatus, stageColor, statusColor, statusToStage } from '@constants/applicationStatus';
import { jobApplicantsList } from '@constants/graphQLQueries';
import { candidateDisplayName } from '@helpers/candidateRecord';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import { Box, Button, Chip, IconButton, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './index.scss';

// Board loads a bounded, most-recent slice — a Kanban is a working view, not a full export.
const BOARD_SIZE = 300;

type Scope = 'MANAGED' | 'ALL';

const SCOPE_OPTIONS = [
  { value: 'MANAGED', label: 'Premium Hiring' },
  { value: 'ALL', label: 'All' },
];
const STATUS_FILTER_OPTIONS = [{ value: '', label: 'All stages' }, ...STATUS_OPTIONS];

interface Card {
  id: string;
  candidate: string;
  company: string;
  role: string;
  status: string;
  stage: string;
  /** Detail, or the reason on a Closed card: the stage is already the column. */
  detail: string;
  hrIds: string[];
}

const PipelineBoard: React.FC = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<Card[]>([]);
  /** What the server has, versus the BOARD_SIZE slice held here. */
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // An HR only ever sees their own scoped set, so filtering by HR is meaningless for them. An HR
  // Lead sees the team's, so the filter is hers as well as the Super Admin's.
  const superAdmin = hasTeamView(useCurrentUserRole());

  // HR directory (id -> name), loaded once — drives the Assigned-HR filter + card labels.
  const [hrById, setHrById] = useState<Record<string, string>>({});
  // NOTE: this filters JobApplication.assignedHRs — who is working the application — which is a
  // different thing from the dedicated-HR client assignment. It stays open to everyone: regular
  // clients are shared work, so an HR may legitimately look at an application another HR is on.
  // Premium clients are hidden server-side, so nothing sensitive rides on this control.
  const hrSelectOptions = useMemo(
    () => [
      { value: '', label: 'All HRs' },
      ...Object.entries(hrById)
        .map(([id, name]) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [hrById],
  );

  // ── Filters ──────────────────────────────────────────────────────────────
  // Default to the managed pipeline — this board is the Premium Managed Hiring view.
  const [scope, setScope] = useState<Scope>('MANAGED');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('');
  const [hrId, setHrId] = useState('');
  const [search, setSearch] = useState('');

  const hasActiveFilters =
    scope !== 'MANAGED' || !!dateFrom || !!dateTo || !!status || !!hrId || !!search;

  const clearFilters = () => {
    setScope('MANAGED');
    setDateFrom('');
    setDateTo('');
    setStatus('');
    setHrId('');
    setSearch('');
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/users?page=0&size=200');
        const map: Record<string, string> = {};
        (res.data?.content ?? []).forEach((u: any) => {
          map[u._id] =
            u.personalDetails?.fullName ||
            `${u.personalDetails?.firstName ?? ''} ${u.personalDetails?.lastName ?? ''}`.trim() ||
            u.personalDetails?.email ||
            'HR';
        });
        setHrById(map);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  // Server-side filters — rebuild the query whenever a server filter changes.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filter: string[] = [];
      if (scope === 'MANAGED') filter.push(`{ key: "job.hiringMode", value: "MANAGED" }`);
      if (dateFrom) filter.push(`{ key: "createdAtFrom", value: "${dateFrom}" }`);
      if (dateTo) filter.push(`{ key: "createdAtTo", value: "${dateTo}" }`);
      if (status) filter.push(`{ key: "status", value: "${status}" }`);
      if (hrId) filter.push(`{ key: "assignedHRs", value: "${hrId}" }`);

      const { data } = await fetchGraphQLApiData(jobApplicantsList(0, BOARD_SIZE, filter));
      const content = data?.getAllJobApplications?.content ?? [];
      setTotalAvailable(data?.getAllJobApplications?.totalElements ?? content.length);
      const mapped: Card[] = content.filter(Boolean).map((item: any) => ({
        id: item._id,
        candidate: candidateDisplayName(item, 'Candidate'),
        company: item.job?.companyName || '-',
        role: item.job?.title || '-',
        status: item.status || '',
        stage: statusToStage(item.status),
        detail: (() => {
          const r = resolveStatus(item.status, item.subStatus, item.closedReason);
          if (r.stage?.label === 'Closed') return r.closedReason;
          return r.detail && r.detail !== r.stage?.label ? r.detail : '';
        })(),
        // The candidate's own recruiter when set; the older assignment otherwise.
        hrIds: item.ownerId ? [item.ownerId] : item.assignedHRs ?? [],
      }));
      setCards(mapped);
    } catch {
      setError('Failed to load the pipeline. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [scope, dateFrom, dateTo, status, hrId]);

  useEffect(() => {
    load();
  }, [load]);

  /*
   * Client-side text search over the loaded slice.
   *
   * `cards` is capped at BOARD_SIZE, so this searches the most recent N and no further. That is a
   * deliberate trade for a board, but it was silent: a candidate outside the cap simply returned
   * nothing, indistinguishable from not existing. The board says so now when the cap is reached.
   */
  const filteredCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) =>
      `${c.candidate} ${c.company} ${c.role}`.toLowerCase().includes(q),
    );
  }, [cards, search]);

  const byStage = useMemo(() => {
    const map: Record<string, Card[]> = {};
    APPLICATION_STAGES.forEach((s) => (map[s.stage] = []));
    filteredCards.forEach((c) => {
      (map[c.stage] ??= []).push(c);
    });
    return map;
  }, [filteredCards]);

  /**
   * Phone: seven columns cannot coexist at 390px, and a horizontal scroller
   * would hide the stages that matter. The board pages through one stage at a
   * time instead, with a segment strip showing where you are.
   */
  const isPhone = useMediaQuery('(max-width: 767px)');
  const [stageIndex, setStageIndex] = useState(0);
  const visibleStages = isPhone ? [APPLICATION_STAGES[stageIndex]] : APPLICATION_STAGES;
  /*
   * Closed is the exit, not a step, and on a busy board it is the longest column by far. Folded
   * to its count by default so the live stages stay in view; one click opens it.
   */
  const [closedOpen, setClosedOpen] = useState(false);
  const activeStage = APPLICATION_STAGES[stageIndex];

  return (
    <Box className="pipeline-board-container">
      <CRMPageTemplate
        title="Pipeline Board"
        icon={<ViewKanbanOutlinedIcon />}
        totalCount={filteredCards.length}
        /* Instant, client-side filtering over cards already fetched — the inline slot, not the
           staged panel. This row used to sit inside the template's children while the template
           also offered its own filter panel, so the page had two filter UIs. */
        inlineFilters={(
          <>
          <SelectFormField
            required={false}
            name="scope"
            label="Scope"
            size="small"
            value={scope}
            options={SCOPE_OPTIONS}
            onChange={(_, v) => setScope(v as Scope)}
          />
          <FormField
            required={false}
            name="dateFrom"
            label="Applied from"
            value={dateFrom}
            onChange={(_, v) => setDateFrom(String(v ?? ''))}
            textFieldProps={{ type: 'date', size: 'small' }}
          />
          <FormField
            required={false}
            name="dateTo"
            label="Applied to"
            value={dateTo}
            onChange={(_, v) => setDateTo(String(v ?? ''))}
            textFieldProps={{ type: 'date', size: 'small' }}
          />
          <SelectFormField
            required={false}
            name="status"
            label="Stage"
            size="small"
            value={status}
            options={STATUS_FILTER_OPTIONS}
            onChange={(_, v) => setStatus(String(v))}
          />
          {superAdmin && (
            <SelectFormField
              required={false}
              name="assignedHr"
              label="Assigned HR"
              size="small"
              value={hrId}
              options={hrSelectOptions}
              onChange={(_, v) => setHrId(String(v))}
            />
          )}
          <Box className="cc-inline-filter--search">
            <FormField
              required={false}
              name="search"
              label="Search"
              value={search}
              placeholder="Candidate / company / role"
              onChange={(_, v) => setSearch(String(v ?? ''))}
              textFieldProps={{ size: 'small' }}
            />
          </Box>
            {hasActiveFilters && (
              <Button size="small" onClick={clearFilters}>
                Clear
              </Button>
            )}
            {/* No silent cap. Searching a candidate outside the loaded slice returned nothing,
                which reads identically to "no such candidate". */}
            {totalAvailable > cards.length && (
              <Typography className="pb-cap-notice">
                {`Board shows the ${cards.length} most recent of ${totalAvailable}. Search covers those.`}
              </Typography>
            )}
          </>
        )}
      >
        {error ? (
          <ErrorMessage message={error} />
        ) : loading ? (
          <SkeletonLoader />
        ) : (
          <Box className="pb-board">
            {/* Phone-only stage pager. Hidden by CSS at 768px and up. */}
            <Box className="pb-stage-pager">
              <IconButton
                size="small"
                aria-label="Previous stage"
                disabled={stageIndex === 0}
                onClick={() => setStageIndex((i) => Math.max(0, i - 1))}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Box className="pb-stage-pager__label">
                <span className="pb-stage-pager__name">{activeStage?.stage}</span>
                <span className="pb-stage-pager__count">
                  {(byStage[activeStage?.stage] ?? []).length}
                </span>
              </Box>
              <IconButton
                size="small"
                aria-label="Next stage"
                disabled={stageIndex === APPLICATION_STAGES.length - 1}
                onClick={() => setStageIndex((i) => Math.min(APPLICATION_STAGES.length - 1, i + 1))}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box className="pb-stage-progress" aria-hidden="true">
              {APPLICATION_STAGES.map((stage, i) => (
                <span
                  key={stage.stage}
                  className={`pb-stage-progress__seg${i === stageIndex ? ' is-active' : ''}`}
                />
              ))}
            </Box>

            {visibleStages.map((stage) => {
              const items = byStage[stage.stage] ?? [];
              const headSx = stageColor(stage.stage);
              return (
                <Box key={stage.stage} className="pb-column">
                  <Box
                    className="pb-column-head"
                    sx={{ backgroundColor: headSx.backgroundColor, color: headSx.color }}
                  >
                    <span className="pb-column-title">{stage.stage}</span>
                    <span className="pb-column-count">{items.length}</span>
                  </Box>
                  <Box className="pb-column-body">
                    {stage.stage === 'Closed' && !closedOpen && !isPhone ? (
                      <Button size="small" onClick={() => setClosedOpen(true)} disabled={items.length === 0}>
                        {items.length === 0 ? 'None closed' : `Show ${items.length} closed`}
                      </Button>
                    ) : items.length === 0 ? (
                      /* Compact, because this sits in a kanban column roughly 260px wide and
                         the page-filling padding would push the next column's cards out of
                         alignment. It was a bare "-", which reads as missing data rather than
                         an empty stage. */
                      <EmptyState compact title="None at this stage" />
                    ) : (
                      items.map((c) => {
                        const hrNames = c.hrIds.map((id) => hrById[id] || 'HR');
                        return (
                          <Box
                            key={c.id}
                            className="pb-card"
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate(`/job-applications/${c.id}`)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') navigate(`/job-applications/${c.id}`);
                            }}
                          >
                            <Typography className="pb-card-candidate">{c.candidate}</Typography>
                            <Typography className="pb-card-role" variant="body2">
                              {c.role} · {c.company}
                            </Typography>
                            <Box className="pb-card-foot">
                              <Chip
                                size="small"
                                label={c.detail || c.stage || c.status || '-'}
                                sx={{
                                  backgroundColor: statusColor(c.status).backgroundColor,
                                  color: statusColor(c.status).color,
                                }}
                              />
                              {hrNames.length > 0 && (
                                <span className="pb-card-hr">{hrNames.join(', ')}</span>
                              )}
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CRMPageTemplate>
    </Box>
  );
};

export default PipelineBoard;
